import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import axios from "axios";
import DatePicker, { registerLocale } from "react-datepicker";
import fr from "date-fns/locale/fr";
import "react-datepicker/dist/react-datepicker.css";
import { baseUrl } from "../config";
import { Error, Button, Label } from "./styles";
import {
  TextField,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import AsyncSelect from "react-select/async";
import {
  DateIRLToErache,
  EracheMonths,
  ParseEracheDateFromText,
  EracheToDateIRL,
  GetEracheDateComponents,
  apiDateToDate,
  formatEventDateLocal,
} from "../utils/dateConverters";
import CategorySelection from "./CategorySelection";
import { groupEventCategoriesFromApi } from "../utils/eventCategories";

registerLocale("fr", fr);

const loadEvents = (inputValue) => {
  if (!inputValue || inputValue.length < 2) return Promise.resolve([]);
  return axios
    .get(baseUrl + "/events", {
      withCredentials: true,
      params: { name: inputValue, pageSize: 50 },
    })
    .then((res) => {
      const events = res.data.data || [];
      return events.map((e) => ({
        value: e.id,
        label: `${e.name} (${formatEventDateLocal(e.date)})`,
        ...e,
      }));
    })
    .catch(() => []);
};

function buildCategoryIdToKey(grouped) {
  const map = {};
  const pairs = [
    ["actorsCategories", "actors"],
    ["actorsCommunityCategories", "actors"],
    ["rpCategories", "rp"],
    ["rpCommunityCategories", "rp"],
    ["hrpCategories", "hrp"],
    ["hrpCommunityCategories", "hrp"],
    ["localizationCategories", "localization"],
    ["localizationCommunityCategories", "localization"],
  ];
  pairs.forEach(([k, metaKey]) => {
    (grouped[k] || []).forEach((c) => {
      map[c.value] = metaKey;
    });
  });
  return map;
}

function categoriesToSelection(categories, grouped, idToKey) {
  const customOptions = grouped.customOptions || [];
  const customIds = new Set(
    customOptions.flatMap((g) => (g.options || []).map((c) => c.value ?? c.id))
  );
  const actors = [];
  const rp = [];
  const hrp = [];
  const localization = [];
  const custom = [];
  (categories || []).forEach((cat) => {
    const id = cat.id ?? cat.value;
    if (customIds.has(id)) {
      custom.push({ value: id, label: cat.name || cat.label });
    } else if (idToKey[id] === "actors") actors.push(id);
    else if (idToKey[id] === "rp") rp.push(id);
    else if (idToKey[id] === "hrp") hrp.push(id);
    else if (idToKey[id] === "localization") localization.push(id);
  });
  return { actors, rp, hrp, localization, custom, officialStatus: "" };
}

function mergeEventCategoriesIntoSelection(selection, eventCategories, idToKey, grouped) {
  if (!eventCategories?.length) return selection;
  const next = {
    actors: [...(selection.actors || [])],
    rp: [...(selection.rp || [])],
    hrp: [...(selection.hrp || [])],
    localization: [...(selection.localization || [])],
    custom: [...(selection.custom || [])],
    officialStatus: selection.officialStatus || "",
  };
  const customIds = new Set(
    (grouped.customOptions || []).flatMap((g) => (g.options || []).map((c) => c.value ?? c.id))
  );
  eventCategories.forEach((cat) => {
    const id = cat.id ?? cat.value;
    const key = idToKey[id];
    if (key && !next[key].includes(id)) {
      next[key].push(id);
    } else if (customIds.has(id) && !next.custom.some((c) => (c.value ?? c.id) === id)) {
      next.custom.push({ value: id, label: cat.name || cat.label });
    }
  });
  return next;
}

const CreatePeriod = () => {
  const { periodId } = useParams();
  const [name, setName] = useState("");
  const [startDate, setStartDate] = useState(() => new Date());
  const [endDate, setEndDate] = useState(() => new Date());
  const [computedStartErache, setComputedStartErache] = useState("");
  const [computedEndErache, setComputedEndErache] = useState("");
  const [startInputMode, setStartInputMode] = useState("irl");
  const [endInputMode, setEndInputMode] = useState("irl");
  const [startEracheDay, setStartEracheDay] = useState(1);
  const [startEracheMonth, setStartEracheMonth] = useState(1);
  const [startEracheYear, setStartEracheYear] = useState(0);
  const [startEracheFreeText, setStartEracheFreeText] = useState("");
  const [endEracheDay, setEndEracheDay] = useState(1);
  const [endEracheMonth, setEndEracheMonth] = useState(1);
  const [endEracheYear, setEndEracheYear] = useState(0);
  const [endEracheFreeText, setEndEracheFreeText] = useState("");
  const [boundaryStartId, setBoundaryStartId] = useState(null);

  const [startEventOption, setStartEventOption] = useState(null);
  const [endEventOption, setEndEventOption] = useState(null);

  const [rpDescription, setRPDescription] = useState("");
  const [hrpDescription, setHRPDescription] = useState("");

  const [groupedCategories, setGroupedCategories] = useState(() => groupEventCategoriesFromApi([]));
  const [categorySelection, setCategorySelection] = useState({
    actors: [],
    rp: [],
    hrp: [],
    localization: [],
    custom: [],
    officialStatus: "",
  });

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [categoryError, setCategoryError] = useState("");

  const noEnd = endInputMode === "none";

  const idToKey = useMemo(() => buildCategoryIdToKey(groupedCategories), [groupedCategories]);

  const applyCategoryPayload = useCallback((data) => {
    const g = groupEventCategoriesFromApi(data);
    setGroupedCategories(g);
  }, []);

  useEffect(() => {
    if (periodId) return;
    axios
      .get(baseUrl + "/event-categories")
      .then((res) => applyCategoryPayload(res.data))
      .catch(() => setError("Erreur chargement catégories."));
  }, [periodId, applyCategoryPayload]);

  useEffect(() => {
    if (!periodId) return;
    Promise.all([
      axios.get(baseUrl + "/event-categories"),
      axios.get(`${baseUrl}/periods/${periodId}`, { withCredentials: true }),
    ])
      .then(([catRes, periodRes]) => {
        applyCategoryPayload(catRes.data);
        const p = periodRes.data.data;
        const grouped = groupEventCategoriesFromApi(catRes.data || []);
        const officialCats = grouped.officialCategories || [];
        const officialIds = new Set(officialCats.map((o) => o.value));
        setName(p.name || "");
        setRPDescription(p.rpDescription || "");
        setHRPDescription(p.hrpDescription || "");
        const startName = (p.startEventName || "").trim();
        const endName = (p.endEventName || "").trim();
        setStartInputMode(startName.startsWith("Début —") ? "irl" : "event");
        if (!p.endEventId) {
          setEndInputMode("none");
        } else {
          setEndInputMode(endName.startsWith("Fin —") ? "irl" : "event");
        }
        setBoundaryStartId(p.startEventId);

        const sd = apiDateToDate(p.startDate);
        if (sd) {
          setStartDate(sd);
          setComputedStartErache(DateIRLToErache(sd, true));
          const sEr = GetEracheDateComponents(sd);
          setStartEracheDay(sEr.day);
          setStartEracheMonth(sEr.month);
          setStartEracheYear(sEr.year);
        }
        if (p.endDate) {
          const ed = apiDateToDate(p.endDate);
          if (ed) {
            setEndDate(ed);
            setComputedEndErache(DateIRLToErache(ed, true));
            const eEr = GetEracheDateComponents(ed);
            setEndEracheDay(eEr.day);
            setEndEracheMonth(eEr.month);
            setEndEracheYear(eEr.year);
          }
        }

        const inList = (p.categories || []).map((c) => c.id);
        const offPick = officialCats.map((o) => o.value).find((id) => inList.includes(id));

        const restCats = (p.categories || []).filter((c) => !officialIds.has(c.id));
        const idMap = buildCategoryIdToKey(grouped);
        const sel = categoriesToSelection(restCats, grouped, idMap);
        setCategorySelection({
          ...sel,
          officialStatus: offPick != null ? String(offPick) : "",
        });

        setStartEventOption({
          value: p.startEventId,
          label: `${p.startEventName || "Début"} (${formatEventDateLocal(p.startDate)})`,
          date: p.startDate,
          categories: [],
        });
        if (p.endEventId) {
          setEndEventOption({
            value: p.endEventId,
            label: `${p.endEventName || "Fin"} (${formatEventDateLocal(p.endDate)})`,
            date: p.endDate,
            categories: [],
          });
        } else {
          setEndEventOption(null);
        }
      })
      .catch(() => setError("Impossible de charger la période."));
  }, [periodId, applyCategoryPayload]);

  useEffect(() => {
    const hasEventBoundary = startInputMode === "event" || endInputMode === "event";
    if (Object.keys(idToKey).length === 0 || !hasEventBoundary) return;
    setCategorySelection((prev) => {
      let next = { ...prev };
      if (startInputMode === "event" && startEventOption?.categories?.length) {
        next = mergeEventCategoriesIntoSelection(next, startEventOption.categories, idToKey, groupedCategories);
      }
      if (endInputMode === "event" && endEventOption?.categories?.length) {
        next = mergeEventCategoriesIntoSelection(next, endEventOption.categories, idToKey, groupedCategories);
      }
      return next;
    });
  }, [
    idToKey,
    groupedCategories,
    startInputMode,
    endInputMode,
    startEventOption?.categories,
    endEventOption?.categories,
  ]);

  const handleStartDateChange = (d) => {
    setStartDate(d);
    if (d) {
      setComputedStartErache(DateIRLToErache(d, true));
      const er = GetEracheDateComponents(d);
      setStartEracheDay(er.day);
      setStartEracheMonth(er.month);
      setStartEracheYear(er.year);
    }
  };

  const handleEndDateChange = (d) => {
    setEndDate(d);
    if (d) {
      setComputedEndErache(DateIRLToErache(d, true));
      const er = GetEracheDateComponents(d);
      setEndEracheDay(er.day);
      setEndEracheMonth(er.month);
      setEndEracheYear(er.year);
    }
  };

  const handleStartEracheFreeTextChange = (text) => {
    const parsed = ParseEracheDateFromText(text);
    if (parsed) {
      setStartEracheDay(parsed.day);
      setStartEracheMonth(parsed.month);
      setStartEracheYear(parsed.year);
      const newDate = EracheToDateIRL(Number(parsed.year), Number(parsed.month), Number(parsed.day));
      if (newDate && !isNaN(newDate.getTime())) {
        setStartDate(newDate);
        setComputedStartErache(DateIRLToErache(newDate, true));
      }
    }
  };

  const handleEndEracheFreeTextChange = (text) => {
    const parsed = ParseEracheDateFromText(text);
    if (parsed) {
      setEndEracheDay(parsed.day);
      setEndEracheMonth(parsed.month);
      setEndEracheYear(parsed.year);
      const newDate = EracheToDateIRL(Number(parsed.year), Number(parsed.month), Number(parsed.day));
      if (newDate && !isNaN(newDate.getTime())) {
        setEndDate(newDate);
        setComputedEndErache(DateIRLToErache(newDate, true));
      }
    }
  };

  const handleStartEracheDateChange = (newDay, newMonth, newYear) => {
    const day = newDay !== undefined ? newDay : startEracheDay;
    const month = newMonth !== undefined ? newMonth : startEracheMonth;
    const year = newYear !== undefined ? newYear : startEracheYear;
    setStartEracheDay(day);
    setStartEracheMonth(month);
    setStartEracheYear(year);
    const newDate = EracheToDateIRL(Number(year), Number(month), Number(day));
    if (newDate && !isNaN(newDate.getTime())) {
      setStartDate(newDate);
      setComputedStartErache(DateIRLToErache(newDate, true));
    }
  };

  const handleEndEracheDateChange = (newDay, newMonth, newYear) => {
    const day = newDay !== undefined ? newDay : endEracheDay;
    const month = newMonth !== undefined ? newMonth : endEracheMonth;
    const year = newYear !== undefined ? newYear : endEracheYear;
    setEndEracheDay(day);
    setEndEracheMonth(month);
    setEndEracheYear(year);
    const newDate = EracheToDateIRL(Number(year), Number(month), Number(day));
    if (newDate && !isNaN(newDate.getTime())) {
      setEndDate(newDate);
      setComputedEndErache(DateIRLToErache(newDate, true));
    }
  };

  const handleCategoryChange = (key, val) => {
    setCategorySelection((prev) => ({ ...prev, [key]: val }));
  };

  const handleCreateCustomCategory = async (inputValue) => {
    try {
      const response = await axios.post(
        baseUrl + "/event-categories",
        { name: inputValue, metaCategoryName: "Custom" },
        { withCredentials: true }
      );
      const res = await axios.get(baseUrl + "/event-categories");
      applyCategoryPayload(res.data);
      setCategorySelection((prev) => ({
        ...prev,
        custom: [...(prev.custom || []), { label: response.data.name, value: response.data.id }],
      }));
    } catch {
      setCategoryError("Erreur lors de la création de la catégorie.");
    }
  };

  const handleCreateCommunityCategory = async (metaCategoryName, inputValue) => {
    try {
      const response = await axios.post(
        baseUrl + "/event-categories",
        { name: inputValue, metaCategoryName },
        { withCredentials: true }
      );
      const newCategory = response.data;
      const res = await axios.get(baseUrl + "/event-categories");
      applyCategoryPayload(res.data);
      const key = {
        Acteur: "actors",
        RP: "rp",
        HRP: "hrp",
        Localisation: "localization",
      }[metaCategoryName];
      if (!key) return;
      setCategorySelection((prev) => ({
        ...prev,
        [key]: [...(prev[key] || []), newCategory.id],
      }));
    } catch {
      setCategoryError("Erreur lors de la création de la catégorie.");
    }
  };

  const buildCategoriesPayload = () => {
    const categories = (categorySelection.custom || []).map((c) => c.value ?? c.id);
    if (categorySelection.officialStatus) {
      categories.push(Number(categorySelection.officialStatus));
    }
    categories.push(
      ...(categorySelection.actors || []),
      ...(categorySelection.rp || []),
      ...(categorySelection.hrp || []),
      ...(categorySelection.localization || [])
    );
    return categories;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setCategoryError("");
    if (!name.trim()) {
      setError("Le nom de la période est requis.");
      return;
    }
    if (!categorySelection.officialStatus) {
      setError("Veuillez choisir le statut officiel ou non officiel.");
      return;
    }

    const categories = buildCategoriesPayload();

    try {
      let startDateForCheck = null;
      let endDateForCheck = null;
      let startDateToSend = null;
      let endDateToSend = null;
      let startEventId = null;
      let endEventId = null;

      if (startInputMode === "event") {
        if (!startEventOption?.value) {
          setError("Veuillez sélectionner un événement de début.");
          return;
        }
        startEventId = startEventOption.value;
        startDateForCheck = startEventOption.date ? new Date(startEventOption.date) : null;
      } else if (startInputMode === "erache") {
        const d = EracheToDateIRL(Number(startEracheYear), Number(startEracheMonth), Number(startEracheDay));
        if (!d || isNaN(d.getTime())) {
          setError("Date de début érachienne invalide.");
          return;
        }
        startDateToSend = d;
        startDateForCheck = d;
      } else {
        startDateToSend = startDate;
        startDateForCheck = startDate;
      }

      if (!noEnd) {
        if (endInputMode === "event") {
          if (!endEventOption?.value) {
            setError("Veuillez sélectionner un événement de fin.");
            return;
          }
          endEventId = endEventOption.value;
          endDateForCheck = endEventOption.date ? new Date(endEventOption.date) : null;
        } else if (endInputMode === "erache") {
          const d = EracheToDateIRL(Number(endEracheYear), Number(endEracheMonth), Number(endEracheDay));
          if (!d || isNaN(d.getTime())) {
            setError("Date de fin érachienne invalide.");
            return;
          }
          endDateToSend = d;
          endDateForCheck = d;
        } else {
          endDateToSend = endDate;
          endDateForCheck = endDate;
        }
      }

      if (startEventId && endEventId && startEventId === endEventId) {
        setError("L'événement de début et de fin doivent être différents.");
        return;
      }
      if (startDateForCheck && endDateForCheck && endDateForCheck <= startDateForCheck) {
        setError("La date de fin doit être après la date de début.");
        return;
      }

      const payload = {
        name: name.trim(),
        endOngoing: noEnd,
        rpDescription: rpDescription.trim(),
        hrpDescription: hrpDescription.trim(),
        categories,
      };
      if (startInputMode === "event") {
        payload.startEventId = startEventId;
      } else if (startDateToSend) {
        payload.startDate = startDateToSend.toISOString().slice(0, 16);
      }
      if (!noEnd) {
        if (endInputMode === "event") {
          payload.endEventId = endEventId;
        } else if (endDateToSend) {
          payload.endDate = endDateToSend.toISOString().slice(0, 16);
        }
      } else {
        payload.endEventId = null;
      }

      if (periodId) {
        if (startInputMode !== "event") {
          const startIdForUpdate = boundaryStartId || startEventOption?.value;
          if (!startIdForUpdate) {
            setError("Impossible de mettre à jour la borne de début.");
            return;
          }
          payload.startEventId = startIdForUpdate;
        }
        await axios.put(`${baseUrl}/periods/${periodId}`, payload, { withCredentials: true });
        setMessage("Période modifiée avec succès.");
      } else {
        await axios.post(baseUrl + "/periods", payload, { withCredentials: true });
        setMessage("Période créée avec succès.");
        setName("");
        setStartEventOption(null);
        setEndEventOption(null);
        setRPDescription("");
        setHRPDescription("");
        setStartInputMode("irl");
        setEndInputMode("irl");
        setCategorySelection({
          actors: [],
          rp: [],
          hrp: [],
          localization: [],
          custom: [],
          officialStatus: "",
        });
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || "Erreur lors de l'enregistrement.";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
    }
  };

  return (
    <Container>
      <h1>{periodId ? "Modifier la période" : "Créer une période"}</h1>
      <Form onSubmit={handleSubmit}>
        <TextField
          label="Nom de la période"
          required
          size="small"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
        />

        <PeriodBoundarySection
          aria-labelledby="period-boundary-start-heading"
        >
          <PeriodBoundaryHeading id="period-boundary-start-heading">
            Borne de début
          </PeriodBoundaryHeading>
          <FormRow>
            <FormCol>
              <Label style={{ marginBottom: 8 }}>Début : mode de saisie</Label>
              <RadioGroup
                row
                value={startInputMode}
                onChange={(e) => {
                  const mode = e.target.value;
                  setStartInputMode(mode);
                  if (mode === "erache") {
                    const s = GetEracheDateComponents(startDate);
                    if (s) {
                      setStartEracheDay(s.day);
                      setStartEracheMonth(s.month);
                      setStartEracheYear(s.year);
                    }
                  }
                }}
              >
                <FormControlLabel value="irl" control={<Radio size="small" />} label="Date IRL" />
                <FormControlLabel value="erache" control={<Radio size="small" />} label="Date érachienne" />
                <FormControlLabel value="event" control={<Radio size="small" />} label="Événement existant" />
              </RadioGroup>
            </FormCol>
          </FormRow>

          <Label>{startInputMode === "event" ? "Événement de début" : "Date de début"}</Label>
          {startInputMode === "event" ? (
            <AsyncSelect
              classNamePrefix="react-select"
              cacheOptions
              loadOptions={loadEvents}
              value={startEventOption}
              onChange={setStartEventOption}
              placeholder="Rechercher un événement par nom..."
              noOptionsMessage={() => "Tapez au moins 2 caractères pour rechercher"}
              isClearable
            />
          ) : startInputMode === "erache" ? (
            <InputRow>
              <TextField
                label="Date érachienne (texte libre)"
                value={startEracheFreeText}
                onChange={(e) => {
                  setStartEracheFreeText(e.target.value);
                  handleStartEracheFreeTextChange(e.target.value);
                }}
                placeholder="Ex: Le 12 Pampélune de l'an de grâce 3"
                size="small"
                sx={{ minWidth: 400 }}
              />
              <span style={{ margin: "0 12px", fontWeight: "bold", fontSize: "24px", alignSelf: "center" }}>/</span>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <TextField
                  label="Jour"
                  type="number"
                  inputProps={{ min: 1, max: 64 }}
                  value={startEracheDay}
                  onChange={(e) => handleStartEracheDateChange(Number(e.target.value), undefined, undefined)}
                  size="small"
                />
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel id="period-start-month-label">Mois</InputLabel>
                <Select
                  labelId="period-start-month-label"
                  value={startEracheMonth}
                  label="Mois"
                  onChange={(e) => handleStartEracheDateChange(undefined, Number(e.target.value), undefined)}
                  size="small"
                >
                  {EracheMonths.map((m, idx) => (
                    <MenuItem key={m} value={idx + 1}>
                      {m}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <TextField
                  label="Année"
                  type="number"
                  value={startEracheYear}
                  onChange={(e) => handleStartEracheDateChange(undefined, undefined, Number(e.target.value))}
                  size="small"
                />
              </FormControl>
            </InputRow>
          ) : (
            <DateRow>
              <DatePicker
                showIcon
                selected={startDate}
                onChange={handleStartDateChange}
                showTimeSelect
                timeCaption="Heure"
                dateFormat="yyyy-MM-dd HH:mm"
                locale="fr"
                showYearDropdown
                showMonthDropdown
                scrollableYearDropdown
                yearDropdownItemNumber={30}
              />
              {computedStartErache && <span style={{ color: "var(--text-muted)" }}>Date érachienne : {computedStartErache}</span>}
            </DateRow>
          )}
        </PeriodBoundarySection>

        <PeriodBoundarySection
          $isEnd
          aria-labelledby="period-boundary-end-heading"
        >
          <PeriodBoundaryHeading id="period-boundary-end-heading">
            Borne de fin
          </PeriodBoundaryHeading>
          <FormRow>
            <FormCol>
              <Label style={{ marginBottom: 8 }}>Fin : mode de saisie</Label>
              <RadioGroup
                row
                value={endInputMode}
                onChange={(e) => {
                  const mode = e.target.value;
                  setEndInputMode(mode);
                  if (mode === "none") setEndEventOption(null);
                  if (mode === "erache") {
                    const en = GetEracheDateComponents(endDate);
                    if (en) {
                      setEndEracheDay(en.day);
                      setEndEracheMonth(en.month);
                      setEndEracheYear(en.year);
                    }
                  }
                }}
              >
                <FormControlLabel value="none" control={<Radio size="small" />} label="Sans date de fin" />
                <FormControlLabel value="irl" control={<Radio size="small" />} label="Date IRL" />
                <FormControlLabel value="erache" control={<Radio size="small" />} label="Date érachienne" />
                <FormControlLabel value="event" control={<Radio size="small" />} label="Événement existant" />
              </RadioGroup>
            </FormCol>
          </FormRow>

          {!noEnd && (
            <>
              <Label>{endInputMode === "event" ? "Événement de fin" : "Date de fin"}</Label>
              {endInputMode === "event" ? (
                <AsyncSelect
                  classNamePrefix="react-select"
                  cacheOptions
                  loadOptions={loadEvents}
                  value={endEventOption}
                  onChange={setEndEventOption}
                  placeholder="Rechercher un événement par nom..."
                  noOptionsMessage={() => "Tapez au moins 2 caractères pour rechercher"}
                  isClearable
                />
              ) : endInputMode === "erache" ? (
                <InputRow>
                  <TextField
                    label="Date érachienne (texte libre)"
                    value={endEracheFreeText}
                    onChange={(e) => {
                      setEndEracheFreeText(e.target.value);
                      handleEndEracheFreeTextChange(e.target.value);
                    }}
                    placeholder="Ex: Le 12 Pampélune de l'an de grâce 3"
                    size="small"
                    sx={{ minWidth: 400 }}
                  />
                  <span style={{ margin: "0 12px", fontWeight: "bold", fontSize: "24px", alignSelf: "center" }}>/</span>
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <TextField
                      label="Jour"
                      type="number"
                      inputProps={{ min: 1, max: 64 }}
                      value={endEracheDay}
                      onChange={(e) => handleEndEracheDateChange(Number(e.target.value), undefined, undefined)}
                      size="small"
                    />
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 130 }}>
                    <InputLabel id="period-end-month-label">Mois</InputLabel>
                    <Select
                      labelId="period-end-month-label"
                      value={endEracheMonth}
                      label="Mois"
                      onChange={(e) => handleEndEracheDateChange(undefined, Number(e.target.value), undefined)}
                      size="small"
                    >
                      {EracheMonths.map((m, idx) => (
                        <MenuItem key={m} value={idx + 1}>
                          {m}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <TextField
                      label="Année"
                      type="number"
                      value={endEracheYear}
                      onChange={(e) => handleEndEracheDateChange(undefined, undefined, Number(e.target.value))}
                      size="small"
                    />
                  </FormControl>
                </InputRow>
              ) : (
                <DateRow>
                  <DatePicker
                    showIcon
                    selected={endDate}
                    onChange={handleEndDateChange}
                    showTimeSelect
                    timeCaption="Heure"
                    dateFormat="yyyy-MM-dd HH:mm"
                    locale="fr"
                    showYearDropdown
                    showMonthDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={30}
                  />
                  {computedEndErache && <span style={{ color: "var(--text-muted)" }}>Date érachienne : {computedEndErache}</span>}
                </DateRow>
              )}
            </>
          )}
        </PeriodBoundarySection>

        <CategorySelection
          variant="event"
          value={{
            actors: categorySelection.actors,
            rp: categorySelection.rp,
            hrp: categorySelection.hrp,
            localization: categorySelection.localization,
            custom: categorySelection.custom,
            officialStatus: String(categorySelection.officialStatus || ""),
          }}
          onChange={handleCategoryChange}
          groupedCategories={{
            actorsCategories: groupedCategories.actorsCategories,
            rpCategories: groupedCategories.rpCategories,
            hrpCategories: groupedCategories.hrpCategories,
            localizationCategories: groupedCategories.localizationCategories,
            actorsCommunityCategories: groupedCategories.actorsCommunityCategories,
            rpCommunityCategories: groupedCategories.rpCommunityCategories,
            hrpCommunityCategories: groupedCategories.hrpCommunityCategories,
            localizationCommunityCategories: groupedCategories.localizationCommunityCategories,
            customOptions: groupedCategories.customOptions,
            officialCategories: groupedCategories.officialCategories,
          }}
          onCreateCustomCategory={handleCreateCustomCategory}
          onCreateCommunityCategory={handleCreateCommunityCategory}
          error={categoryError}
        />
        <Label>Description RP</Label>
        <TextField
          value={rpDescription}
          multiline
          rows={3}
          onChange={(e) => setRPDescription(e.target.value)}
          placeholder="Description RP de la période"
          fullWidth
        />
        <Label>Description HRP</Label>
        <TextField
          value={hrpDescription}
          multiline
          rows={3}
          onChange={(e) => setHRPDescription(e.target.value)}
          placeholder="Description HRP de la période"
          fullWidth
        />
        <Button
          type="submit"
          style={{
            alignSelf: "center",
            width: "min(100%, 240px)",
            justifyContent: "center",
          }}
        >
          {periodId ? "Enregistrer" : "Créer la période"}
        </Button>
        {message && <Success>{message}</Success>}
        {error && <Error>{error}</Error>}
      </Form>
    </Container>
  );
};

export default CreatePeriod;

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 30px;
`;
const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;
const DateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;
const FormRow = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
`;
const FormCol = styled.div`
  flex: 1 1 200px;
  min-width: 200px;
`;

const PeriodBoundarySection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border-radius: var(--radius-md, 8px);
  border: 1px solid var(--border);
  background: var(--surface-2, transparent);

  ${(p) =>
    p.$isEnd &&
    `
    margin-top: 8px;
    padding-top: 20px;
    border-top-width: 2px;
    border-top-color: var(--border);
  `}
`;

const PeriodBoundaryHeading = styled.h2`
  margin: 0 0 4px;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: var(--text-muted);
  text-transform: uppercase;
`;

const InputRow = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
  flex-wrap: wrap;
  align-items: flex-end;
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;
const Success = styled.div`
  color: green;
  margin-top: 8px;
`;
